import type { NodeDisplay } from '@/versions/v1/schema/node-display';
import type { VizTone } from '@/versions/v1/modules/_viz';
import {
  NODE_DISPLAYS,
  NODE_DISPLAYS_UNSEEDED,
} from '@/versions/v1/shared/bunker-hill-seed';

export type RateVsDesignView = Extract<NodeDisplay, { kind: 'rate-vs-design' }> & {
  tone: VizTone;
};

export type TrendVsTargetView = Extract<NodeDisplay, { kind: 'trend-vs-target' }> & {
  synthetic: boolean;
  series: readonly { name: string; points: readonly number[]; tone: VizTone }[];
  references: readonly { value: number; label: string; tone: VizTone }[];
};

export type LiveVsDesignView = Extract<NodeDisplay, { kind: 'live-vs-design' }>;

export type NodeDisplayView = RateVsDesignView | TrendVsTargetView | LiveVsDesignView;

export function rateVsDesignTone(value: number, design: number): VizTone {
  const delta = Math.abs(value - design);
  if (delta <= design * 0.05) return 'ok';
  return 'warn';
}

function referenceTone(label: string): VizTone {
  if (/alarm|high/i.test(label)) return 'bad';
  return 'neutral';
}

/** Categorical series identity — Zn warn · Pb info · Ag ok (recovery trend palette). */
function trendSeriesTone(name: string, index: number): VizTone {
  const n = name.toLowerCase();
  if (/\bag\b/.test(n) || n.includes('ag ')) return 'ok';
  if (n.includes('pb')) return 'info';
  if (n.includes('zn')) {
    // Distinguish rougher vs cleaner when both are Zn-labelled (nd-zn-ph).
    if (n.includes('cleaner') || n.includes('at-176')) return 'info';
    return 'warn';
  }
  const palette: VizTone[] = ['info', 'warn', 'ok', 'neutral'];
  return index === 0 ? 'neutral' : palette[(index - 1) % palette.length];
}

export type NodeDisplayKindFilter = 'tables' | 'widgets';

/** Per-node C2 displays as view models; `only` splits tables (live-vs-design) from widgets (meters, trends). */
export function nodeDisplays(
  nodeId: string,
  options?: { tableLast?: boolean; only?: NodeDisplayKindFilter },
): NodeDisplayView[] {
  const rows = NODE_DISPLAYS.filter((row) => row.subprocessId === nodeId).map((display) => {
    if (display.kind === 'rate-vs-design') {
      return {
        ...display,
        tone: rateVsDesignTone(display.value, display.design),
      };
    }
    if (display.kind === 'trend-vs-target') {
      const synthetic = display.provenance.includes('[syn]');
      return {
        ...display,
        synthetic,
        series: display.series.map((row, index) => ({
          ...row,
          tone: trendSeriesTone(row.name, index),
        })),
        references: display.references.map((row) => ({
          ...row,
          tone: referenceTone(row.label),
        })),
      };
    }
    return display;
  });

  const tables = rows.filter((row) => row.kind === 'live-vs-design');
  const rest = rows.filter((row) => row.kind !== 'live-vs-design');
  if (options?.only === 'tables') return tables;
  if (options?.only === 'widgets') return rest;
  if (!options?.tableLast) return rows;
  return [...rest, ...tables];
}

export function unseededDisplays(nodeId: string): readonly { title: string; tagIds: readonly string[] }[] {
  return NODE_DISPLAYS_UNSEEDED.filter((row) => row.subprocessId === nodeId);
}

export function unseededLine(nodeId: string): string | null {
  const rows = unseededDisplays(nodeId);
  if (rows.length === 0) return null;
  const titles = rows.map((row) => row.title).join(' / ');
  return `Awaiting historian: ${titles}`;
}

/** A display's card heading: the seed title with its C2 kind suffix moved to
 *  the meta slot — "Cyclone feed … ratio (live vs design table)" reads as
 *  "Cyclone feed … ratio" over "live vs design" (polish.P11). */
export function displayHeading(title: string): { title: string; meta: string | null } {
  const match = /\s*\((live vs design) table\)$/i.exec(title);
  return match ? { title: title.slice(0, match.index), meta: match[1]! } : { title, meta: null };
}

/** A trend card's caption: its reference lines, stated once over the
 *  sparkline instead of printed on it ("Design 186 stph (248% of new feed)"). */
export function trendCaption(display: TrendVsTargetView): string | null {
  return display.references.length ? display.references.map((ref) => ref.label).join(' · ') : null;
}

/** The widget column's two rows: rate cards read across it, trend cards pair up beneath. */
export function widgetRows(displays: readonly NodeDisplayView[]): {
  across: NodeDisplayView[];
  paired: NodeDisplayView[];
} {
  return {
    across: displays.filter((display) => display.kind !== 'trend-vs-target'),
    paired: displays.filter((display) => display.kind === 'trend-vs-target'),
  };
}
